import { AxiosResponse } from "axios";
import { createAxiosInstance } from "./AxiosService";
import { getCurrentUserId, isAdmin } from "./AuthService";

const getAxiosInstance = () => createAxiosInstance();

export interface Email {
  id: string;
  email: string;
  verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserProfileEmail {
  id: string;
  user_id: string;
  email: Email;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  emails: UserProfileEmail[];
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  emails: Array<{
    email: string;
    is_primary: boolean;
  }>;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
}

export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  username?: string;
  password?: string;
}

export interface PaginationParams {
  page?: number;
  per_page?: number;
  search?: string;
}

export interface PaginatedResponse<T> {
  users: T[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export class UserService {
  // Create user (admin only)
  static async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const response: AxiosResponse<User> = await getAxiosInstance().post(
        "/user/users/",
        userData,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to create user");
    }
  }

  // Get user by ID (own data or admin)
  static async getUserById(userId: string): Promise<User> {
    try {
      const response: AxiosResponse<User> = await getAxiosInstance().get(
        `/user/users/${userId}`,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to fetch user");
    }
  }

  // Get user by username (admin only)
  static async getUserByUsername(username: string): Promise<User> {
    try {
      const response: AxiosResponse<User> = await getAxiosInstance().get(
        `/user/users/by_username`,
        {
          params: { username },
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch user by username",
      );
    }
  }

  // Get user by email (admin only)
  static async getUserByEmail(email: string): Promise<User> {
    try {
      const response: AxiosResponse<User> = await getAxiosInstance().get(
        `/user/users/by_email`,
        {
          params: { email },
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch user by email",
      );
    }
  }

  // Get paginated users (admin only)
  static async getUsers(
    params: PaginationParams = {},
  ): Promise<PaginatedResponse<User>> {
    try {
      const response: AxiosResponse<PaginatedResponse<User>> =
        await getAxiosInstance().get("/user/users/", {
          params,
        });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to fetch users");
    }
  }

  // Alias for getUsers (for backward compatibility)
  static async getAllUsers(
    params: PaginationParams = {},
  ): Promise<PaginatedResponse<User>> {
    return this.getUsers(params);
  }

  // Update user (own data or admin)
  static async updateUser(
    userId: string,
    userData: UpdateUserRequest,
  ): Promise<User> {
    try {
      const response: AxiosResponse<User> = await getAxiosInstance().put(
        `/user/users/${userId}`,
        userData,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to update user");
    }
  }

  // Delete user (admin only)
  static async deleteUser(userId: string): Promise<void> {
    try {
      await getAxiosInstance().delete(`/user/users/${userId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to delete user");
    }
  }

  // Get current user profile
  static async getCurrentUserProfile(): Promise<User> {
    try {
      const response: AxiosResponse<User> = await getAxiosInstance().get(
        "/user/users/profile",
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch user profile",
      );
    }
  }

  // Update current user profile
  static async updateCurrentUserProfile(
    userData: UpdateUserRequest,
  ): Promise<User> {
    try {
      const response: AxiosResponse<User> = await getAxiosInstance().put(
        "/user/users/profile",
        userData,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to update user profile",
      );
    }
  }

  // Check if current user can access specific user data
  static canAccessUser(userId: string): boolean {
    const currentUserId = getCurrentUserId();
    return currentUserId === userId || isAdmin();
  }

  // Utility methods for common operations
  static async getCurrentUser(): Promise<User | null> {
    try {
      return await this.getCurrentUserProfile();
    } catch (error) {
      console.error("Failed to get current user:", error);
      return null;
    }
  }

  static async updateCurrentUser(userData: UpdateUserRequest): Promise<User> {
    return this.updateCurrentUserProfile(userData);
  }

  static async searchUsers(searchTerm: string, page = 1, perPage = 10) {
    return this.getUsers({
      search: searchTerm,
      page,
      per_page: perPage,
    });
  }

  static isAvailable(): boolean {
    return true; // User service is always available
  }
}

// Backward compatibility functions
export interface LegacyUser {
  username: string | null;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  password: string;
}

export const registerUser = async (user: LegacyUser): Promise<boolean> => {
  // Destructure for potential future use, but currently unused due to admin-only registration
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { username, firstName, lastName, dateOfBirth, email, password } = user;

  try {
    // Since user creation is now admin-only, this should throw an error
    // or there should be a separate public registration endpoint
    throw new Error(
      "User registration is now admin-only. Please contact an administrator.",
    );
  } catch (error: any) {
    throw new Error(error.message || "An unexpected error occurred.");
  }
};

export const getUserDetails = async (token: string) => {
  try {
    const user = await UserService.getCurrentUserProfile();
    return user;
  } catch (error: any) {
    console.error("Failed to fetch user details:", error);
    throw new Error("Unable to retrieve user details");
  }
};

export default UserService;
